"use client";

import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";

import { type Value, type EditableProps, KEYS, NormalizeTypesPlugin } from "platejs";
import { createPlatePlugin, Plate, usePlateEditor } from "platejs/react";
import { useRef } from "react";

import "../styles/output.css";
import { Toaster } from "sonner";
import { Providers } from "../components/context/providers";
import { EditorKit } from "../components/editor/editor-kit";
import { EditorContainer, Editor } from "../components/ui/editor";
import { FixedToolbar } from "../components/ui/fixed-toolbar";
import { FixedToolbarButtons } from "../components/ui/custom-fixed-toolbar-buttons";
import { LinkPlugin } from "@platejs/link/react";
import { LinkRules } from "@platejs/link";
import { LinkElement } from "../components/ui/link-node";
import { LinkFloatingToolbar } from "../components/ui/link-toolbar";
import { UploadContextType, UploadProvider } from "./uploadContext";

export type Mode = "view" | "view-edit" | "edit";

export const defaultInitialValue: Value = [
  {
    type: "p",
    children: [{ text: "" }],
  },
];

export const EmptyDoc: Value = [
  {
    type: "p",
    children: [{ text: "" }],
  },
];

type EditorMode = "minimal" | "basic" | "standard" | "full" | "collaboration" | "custom";

export interface WcPlateEditorProps {
  initialValue?: Value;
  onChange?: (value: Value) => void;
  placeholder?: string;
  editorHeight?: number | string;
  /**
   * @deprecated Mode is deprecated. Use disabled prop instead.
   */
  mode?: Mode;
  hideToolbar?: boolean;
  cancelLabel?: string;
  saveLabel?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  showFullscreen?: boolean;
  editorMode?: EditorMode;
  uploadProps?: UploadContextType;
  editorProps?: Partial<Omit<EditableProps, "decorate">>;
}

export type ChangeValue = Value;

export const WcPlateEditor = (props: WcPlateEditorProps) => {
  const {
    initialValue,
    onChange,
    placeholder,
    editorHeight = "100%",
    autoFocus = false,
    disabled = false,
    showFullscreen = false,
    editorMode = "full",
    editorProps,
    uploadProps,
  } = props;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const isInitialized = useRef(false);
  const handleEditorChange = useCallback(({ value }: { value: Value }) => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    onChange?.(value);
  }, [onChange]);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [isFullscreen]);

  const fullscreenStyle = isFullscreen
    ? {
        position: "fixed" as const,
        top: 60,
        left: 10,
        right: 10,
        bottom: 10,
        width: "98vw",
        height: "94vh",
        zIndex: 9998,
        overflow: "auto" as const,
        borderStyle: "solid",
        borderWidth: 1,
        borderRadius: "20px",
      }
    : {};

  const handleFullscreenChange = useCallback(() => {
    setIsFullscreen((prev) => {
      return !prev;
    });
  }, [isFullscreen]);

  // Memoize plugin configuration based on editor mode
  const pluginConfig = useMemo(() => {
    switch (editorMode) {
      case "minimal":
        return {
          // Core plugins always enabled
          [KEYS.normalizeTypes]: false,
          // AI features
          // [KEYS.copilot]: false,
          // [KEYS.ai]: false,
          // [KEYS.aiChat]: false,
          // Formatting plugins - only basic marks
          [KEYS.textAlign]: false,
          [KEYS.lineHeight]: false,
          [KEYS.indent]: false,
          // List plugins
          [KEYS.list]: false,
          [KEYS.listClassic]: false,
          // Structure plugins
          [KEYS.toggle]: false,
          [KEYS.table]: false,
          [KEYS.blockquote]: false,
          [KEYS.codeBlock]: false,
          // Media plugins
          [KEYS.img]: false,
          [KEYS.video]: false,
          [KEYS.audio]: false,
          [KEYS.file]: false,
          [KEYS.mediaEmbed]: false,
          // Interactive plugins
          [KEYS.emoji]: false,
          [KEYS.mention]: false,
          [KEYS.comment]: false,
          // Advanced plugins
          [KEYS.equation]: false,
          [KEYS.excalidraw]: false,
          [KEYS.date]: false,
          [KEYS.toc]: false,
          [KEYS.callout]: false,
          // Import/Export
          [KEYS.markdown]: false,
          [KEYS.html]: false,
          [KEYS.docx]: false,
          [KEYS.csv]: false,
        };

      case "basic":
        return {
          [KEYS.normalizeTypes]: false,
          // AI features
          // [KEYS.copilot]: false,
          // [KEYS.ai]: false,
          // [KEYS.aiChat]: false,
          // Formatting plugins - basic formatting enabled
          [KEYS.textAlign]: true,
          [KEYS.lineHeight]: false,
          [KEYS.indent]: false,
          // List plugins - basic lists enabled
          [KEYS.list]: true,
          [KEYS.listClassic]: false,
          // Structure plugins - only link enabled
          [KEYS.toggle]: false,
          [KEYS.table]: false,
          [KEYS.blockquote]: true,
          [KEYS.codeBlock]: false,
          // Media plugins
          [KEYS.img]: false,
          [KEYS.video]: false,
          [KEYS.audio]: false,
          [KEYS.file]: false,
          [KEYS.mediaEmbed]: false,
          // Interactive plugins
          [KEYS.emoji]: false,
          [KEYS.mention]: false,
          [KEYS.comment]: false,
          // Advanced plugins
          [KEYS.equation]: false,
          [KEYS.excalidraw]: false,
          [KEYS.date]: false,
          [KEYS.toc]: false,
          [KEYS.callout]: false,
          // Import/Export
          [KEYS.markdown]: false,
          [KEYS.html]: false,
          [KEYS.docx]: false,
          [KEYS.csv]: false,
        };

      case "standard":
        return {
          [KEYS.normalizeTypes]: false,
          // AI features

          // Formatting plugins
          [KEYS.textAlign]: true,
          [KEYS.lineHeight]: true,
          [KEYS.indent]: true,
          // List plugins
          [KEYS.list]: true,
          [KEYS.listClassic]: true,
          // Structure plugins
          [KEYS.toggle]: true,
          [KEYS.table]: true,
          [KEYS.blockquote]: true,
          [KEYS.codeBlock]: true,
          // Media plugins
          [KEYS.img]: true,
          [KEYS.video]: true,
          [KEYS.audio]: true,
          [KEYS.file]: true,
          [KEYS.mediaEmbed]: true,
          // Interactive plugins
          [KEYS.emoji]: true,
          [KEYS.mention]: false,
          [KEYS.comment]: false,
          // Advanced plugins
          [KEYS.equation]: true,
          [KEYS.excalidraw]: false,
          [KEYS.date]: true,
          [KEYS.toc]: true,
          [KEYS.callout]: true,
          // Import/Export
          [KEYS.markdown]: true,
          [KEYS.html]: true,
          [KEYS.docx]: true,
          [KEYS.csv]: true,
        };

      case "full":
        return {
          [KEYS.normalizeTypes]: false,
          // All plugins enabled for full mode
          // [KEYS.copilot]: true,
          // [KEYS.ai]: true,
          // [KEYS.aiChat]: true,
          [KEYS.textAlign]: true,
          [KEYS.lineHeight]: true,
          [KEYS.indent]: true,
          [KEYS.list]: true,
          [KEYS.listClassic]: true,
          [KEYS.toggle]: true,
          [KEYS.table]: true,
          [KEYS.blockquote]: true,
          [KEYS.codeBlock]: true,
          [KEYS.img]: true,
          [KEYS.video]: true,
          [KEYS.audio]: true,
          [KEYS.file]: true,
          [KEYS.mediaEmbed]: true,
          [KEYS.emoji]: true,
          [KEYS.mention]: false,
          [KEYS.comment]: false,
          [KEYS.equation]: false,
          [KEYS.excalidraw]: true,
          [KEYS.date]: true,
          [KEYS.toc]: true,
          [KEYS.callout]: true,
          [KEYS.markdown]: true,
          [KEYS.html]: true,
          [KEYS.docx]: true,
          [KEYS.csv]: true,
        };

      case "collaboration":
        return {
          [KEYS.normalizeTypes]: false,
          // AI features disabled for collaboration
          // [KEYS.copilot]: false,
          // [KEYS.ai]: false,
          // [KEYS.aiChat]: false,
          // Basic formatting
          [KEYS.textAlign]: true,
          [KEYS.lineHeight]: false,
          [KEYS.indent]: false,
          // List plugins
          [KEYS.list]: true,
          [KEYS.listClassic]: false,
          // Structure plugins - minimal set
          [KEYS.toggle]: false,
          [KEYS.table]: false,
          [KEYS.blockquote]: true,
          [KEYS.codeBlock]: false,
          // Media plugins disabled
          [KEYS.img]: false,
          [KEYS.video]: false,
          [KEYS.audio]: false,
          [KEYS.file]: false,
          [KEYS.mediaEmbed]: false,
          // Interactive plugins - focus on collaboration
          [KEYS.emoji]: false,
          [KEYS.mention]: false,
          [KEYS.comment]: false,
          // Advanced plugins
          [KEYS.equation]: false,
          [KEYS.excalidraw]: false,
          [KEYS.date]: false,
          [KEYS.toc]: false,
          [KEYS.callout]: false,
          // Import/Export
          [KEYS.markdown]: false,
          [KEYS.html]: false,
          [KEYS.docx]: false,
          [KEYS.csv]: false,
        };

      case "custom":
        return {
          [KEYS.normalizeTypes]: false,
          // AI features
          // [KEYS.copilot]: false,
          // [KEYS.ai]: false,
          // [KEYS.aiChat]: false,
          // Formatting plugins
          [KEYS.textAlign]: true,
          [KEYS.lineHeight]: false,
          [KEYS.indent]: false,
          // List plugins
          [KEYS.list]: true,
          [KEYS.listClassic]: false,
          // Structure plugins - selective set
          [KEYS.toggle]: false,
          [KEYS.table]: true,
          [KEYS.blockquote]: true,
          [KEYS.codeBlock]: true,
          // Media plugins
          [KEYS.img]: false,
          [KEYS.video]: false,
          [KEYS.audio]: false,
          [KEYS.file]: false,
          [KEYS.mediaEmbed]: false,
          // Interactive plugins
          [KEYS.emoji]: false,
          [KEYS.mention]: false,
          [KEYS.comment]: false,
          // Advanced plugins
          [KEYS.equation]: false,
          [KEYS.excalidraw]: false,
          [KEYS.date]: false,
          [KEYS.toc]: false,
          [KEYS.callout]: false,
          // Import/Export
          [KEYS.markdown]: false,
          [KEYS.html]: false,
          [KEYS.docx]: false,
          [KEYS.csv]: false,
        };

      default:
        // Default to full mode
        return {
          [KEYS.normalizeTypes]: false,
          // [KEYS.copilot]: true,
          // [KEYS.ai]: true,
          // [KEYS.aiChat]: true,
          [KEYS.textAlign]: true,
          [KEYS.lineHeight]: true,
          [KEYS.indent]: true,
          [KEYS.list]: true,
          [KEYS.listClassic]: true,
          [KEYS.toggle]: true,
          [KEYS.table]: true,
          [KEYS.blockquote]: true,
          [KEYS.codeBlock]: true,
          [KEYS.img]: true,
          [KEYS.video]: true,
          [KEYS.audio]: true,
          [KEYS.file]: true,
          [KEYS.mediaEmbed]: true,
          [KEYS.emoji]: true,
          [KEYS.mention]: false,
          [KEYS.comment]: false,
          [KEYS.equation]: true,
          [KEYS.excalidraw]: true,
          [KEYS.date]: true,
          [KEYS.toc]: true,
          [KEYS.callout]: true,
          [KEYS.markdown]: true,
          [KEYS.html]: false,
          [KEYS.docx]: false,
          [KEYS.csv]: true,
        };
    }
  }, [editorMode, isFullscreen]);

  const toolbarConfig = {
    pluginConfig: pluginConfig,
    isFullscreen: isFullscreen,
    onFullscreenChange: handleFullscreenChange,
    includeFullscreen: showFullscreen,
  };

  const domainAndUrlRegEx = /^(((http|https):\/\/|)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,6}(:[0-9]{1,5})?(\/.*)?)$/;
  const transformInput = (value: string) => {
    if (domainAndUrlRegEx.test(value)) {
      if (value.startsWith("http://") || value.startsWith("https://")) {
        return value;
      } else {
        return `https://${value}`;
      }
    }
    return value;
  };

  const editor = usePlateEditor({
    plugins: [
      ...EditorKit,
      LinkPlugin.configure({
        render: {
          node: LinkElement,
          afterEditable: () => <LinkFloatingToolbar />,
        },
        options: {
          transformInput,
        },
        inputRules: [
          LinkRules.markdown(),
          LinkRules.autolink({ variant: 'paste' }),
          LinkRules.autolink({ variant: 'space' }),
          LinkRules.autolink({ variant: 'break' }),
        ],
      }),
      createPlatePlugin({
        key: "fixed-toolbar",
        render: {
          beforeEditable: () => (
            <FixedToolbar includeFullscreen={toolbarConfig.includeFullscreen}>
              <FixedToolbarButtons {...toolbarConfig} isFullscreen={isFullscreen} />
            </FixedToolbar>
          ),
        },
      }),
      NormalizeTypesPlugin.configure({
        enabled: true,
      }),
    ] as any,
    value: Array.isArray(initialValue) && initialValue.length > 0 ? initialValue : defaultInitialValue,
  });

  return (
    <div
      id="WcPlateEditorStack"
      className="bg-background"
      style={{
        height: editorHeight,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        ...fullscreenStyle,
      }}
    >
      <UploadProvider downloadUrl={uploadProps?.downloadUrl || ""} uploadUrl={uploadProps?.uploadUrl || ""}>
        <Providers>
          <Plate
            editor={editor}
            readOnly={disabled}
            //@ts-ignore
            onValueChange={handleEditorChange}
          >
            <EditorContainer variant={"default"}>
              <Editor
                variant={"fullWidth"}
                placeholder={placeholder}
                autoFocus={autoFocus}
                disabled={false}
                readOnly={disabled}
                className="overflow-y-auto overflow-x-hidden h-full bg-background"
                {...editorProps}
              />
            </EditorContainer>
          </Plate>
        </Providers>
      </UploadProvider>
      <Toaster />
    </div>
  );
};
